import {
  FormikErrorMessage,
  Modal,
  Row,
  Section,
  Button,
  TextInput
} from 'shared'
import { Formik, Form, FormikHelpers, FormikProps } from 'formik'
import { Panel, Col, ButtonToolbar, SelectPicker, Toggle } from 'rsuite'
import { useMemo } from 'react'
import { notifyError, notifySuccess } from 'utils'
import {
  defaultClientFormValues,
  ClientForm,
  CLIENT_FORM_FIELDS,
  getClientConfigValidationSchema,
  getInitialClientFormValueFromResponse
} from '../utils'
import { useGetGrantTypeQuery, useGetIntegrationTypeQuery, useCreateClientMutation, useUpdateClientMutation } from '../clientApiSlice'
import { ClientDetails } from '../types'


interface CreateClientProps {
  open: boolean;
  onClose: () => void;
  isEditMode: boolean;
  client?: ClientDetails | null;
}
const CreateClient: React.FC<CreateClientProps> = ({
  open,
  onClose,
  isEditMode,
  client
}) => {
  const clientId = client?.id
  const initialValues = useMemo(() => {
    const data = isEditMode && client
      ? getInitialClientFormValueFromResponse(client)
      : defaultClientFormValues
    return data
  }, [isEditMode, client])
  const { data: grantTypes } = useGetGrantTypeQuery({})
  const { data: integrationTypes } = useGetIntegrationTypeQuery({})
  const [createClient] = useCreateClientMutation()
  const [updateClient] = useUpdateClientMutation()
  const renderFormButtons = (formikProps: FormikProps<ClientForm>) => (
    <ButtonToolbar>
      {isEditMode ? (
        <>
          <Button
            className="secondary"
            onClick={() => {
              formikProps.resetForm()
            }}
          >
            Reset
          </Button>
          <Button
            className="primary"
            type="submit"
            disabled={formikProps.isValidating || formikProps.isSubmitting}
          >
            Save changes
          </Button>
        </>
      ) : (
        <>
          <Button
            className="secondary"
            onClick={() => {
              formikProps.resetForm()
            }}
          >
            Reset
          </Button>
          <Button
            className="primary"
            type="submit"
            disabled={formikProps.isValidating || formikProps.isSubmitting}
          >
            Save
          </Button>
        </>
      )}
    </ButtonToolbar>
  )

  const GrantTypeOptions = (grantTypes || []).map((t) => ({
    label: t,
    value: t
  }))
  const IntegrationTypeOptions = (integrationTypes || []).map((t) => ({
    label: t,
    value: t
  }))

  const onSubmit = async (
    formValues: ClientForm,
    { setSubmitting }: FormikHelpers<ClientForm>
  ) => {
    const payload = {
      name: formValues.name,
      accountId: formValues.accountId,
      clientId: formValues.clientId,
      clientSecret: formValues.clientSecret,
      userName: formValues.userName,
      password: formValues.password,
      refreshToken: formValues.refreshToken,
      grantType: formValues.grantType,
      integrationType: formValues.integrationType,
      baseUrl: formValues.baseUrl,
      tokenExpiry: formValues.tokenExpiry,
      externalSystem: formValues.externalSystem,
      referenceId: formValues.referenceId,
      active: formValues.active
    }

    try {
      if (isEditMode && clientId) {
        await updateClient({ id: clientId, client: payload }).unwrap()
        notifySuccess('Client updated successfully!')
      } else {
        await createClient(payload).unwrap()
        notifySuccess('Client created successfully!')
      }
      onClose()
    } catch (error) {
      notifyError(isEditMode ? 'Failed to update client.' : 'Failed to create client.', {
        containerId: 'modalToast'
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      secondary
      open={open}
      onClose={onClose}
      title={isEditMode ? 'Edit Client' : 'Add Client'}
      size="calc(50% - 120px)"
      body={
        <Formik
          initialValues={initialValues}
          validationSchema={getClientConfigValidationSchema}
          enableReinitialize
          onSubmit={onSubmit}
        >
          {(formikProps: FormikProps<ClientForm>) => (
            <Form className="create-rule">
              <Panel>
                <Section title="Client Details">
                  <Row>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={CLIENT_FORM_FIELDS.NAME}
                        placeholder="Enter client name"
                        dataType="string"
                      />
                      <FormikErrorMessage name={CLIENT_FORM_FIELDS.NAME} />
                    </Col>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={CLIENT_FORM_FIELDS.BASE_URL}
                        placeholder="Enter base url"
                        dataType="string"
                      />
                      <FormikErrorMessage name={CLIENT_FORM_FIELDS.BASE_URL} />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={CLIENT_FORM_FIELDS.CLIENT_ID}
                        placeholder="Enter ClientId"
                        dataType="string"
                      />
                      <FormikErrorMessage name={CLIENT_FORM_FIELDS.CLIENT_ID} />
                    </Col>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={CLIENT_FORM_FIELDS.CLIENT_SECRET}
                        placeholder="Enter ClientSecret"
                        dataType="string"
                      />
                      <FormikErrorMessage
                        name={CLIENT_FORM_FIELDS.CLIENT_SECRET}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={CLIENT_FORM_FIELDS.USER_NAME}
                        placeholder="Enter user name"
                        dataType="string"
                      />
                      <FormikErrorMessage name={CLIENT_FORM_FIELDS.USER_NAME} />
                    </Col>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={CLIENT_FORM_FIELDS.PASSWORD}
                        placeholder="Enter password"
                        dataType="string"
                      />
                      <FormikErrorMessage name={CLIENT_FORM_FIELDS.PASSWORD} />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <SelectPicker
                        data={GrantTypeOptions}
                        placeholder="Select Grant Type"
                        value={formikProps.values[CLIENT_FORM_FIELDS.GRANT_TYPE]}
                        onChange={(value) =>
                          formikProps.setFieldValue(
                            CLIENT_FORM_FIELDS.GRANT_TYPE,
                            value
                          )
                        }
                        cleanable
                        searchable={false}
                        style={{ width: '100%' }}
                      />
                      <FormikErrorMessage name={CLIENT_FORM_FIELDS.GRANT_TYPE} />
                    </Col>
                    <Col xs={12}>
                      <SelectPicker
                        data={IntegrationTypeOptions}
                        placeholder="Select Integration Type"
                        value={formikProps.values[CLIENT_FORM_FIELDS.INTEGRATION_TYPE]}
                        onChange={(value) =>
                          formikProps.setFieldValue(
                            CLIENT_FORM_FIELDS.INTEGRATION_TYPE,
                            value
                          )
                        }
                        cleanable
                        searchable={false}
                        style={{ width: '100%' }}
                      />
                      <FormikErrorMessage name={CLIENT_FORM_FIELDS.INTEGRATION_TYPE} />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={CLIENT_FORM_FIELDS.REFRESH_TOKEN}
                        placeholder="Enter refresh token"
                        dataType="string"
                      />
                      <FormikErrorMessage
                        name={CLIENT_FORM_FIELDS.REFRESH_TOKEN}
                      />
                    </Col>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={CLIENT_FORM_FIELDS.ACCOUNT_ID}
                        placeholder="Enter account ID"
                        dataType="string"
                      />
                      <FormikErrorMessage
                        name={CLIENT_FORM_FIELDS.ACCOUNT_ID}
                      />
                    </Col>

                  </Row>
                  <Row>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={CLIENT_FORM_FIELDS.TOKEN_EXPIRY}
                        placeholder="Enter token expiry"
                        dataType="number"
                      />
                      <FormikErrorMessage
                        name={CLIENT_FORM_FIELDS.TOKEN_EXPIRY}
                      />
                    </Col>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={CLIENT_FORM_FIELDS.EXTERNAL_SYSTEM}
                        placeholder="Enter external system"
                        dataType="string"
                      />
                      <FormikErrorMessage
                        name={CLIENT_FORM_FIELDS.EXTERNAL_SYSTEM}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={CLIENT_FORM_FIELDS.REFERENCE_ID}
                        placeholder="Enter reference id"
                        dataType="string"
                      />
                      <FormikErrorMessage
                        name={CLIENT_FORM_FIELDS.REFERENCE_ID}
                      />
                    </Col>
                  </Row>
                  {isEditMode && (
                    <Section title="Status">
                      <Row>
                        <Col xs={24} md={12}>
                          <Toggle
                            checked={formikProps.values[CLIENT_FORM_FIELDS.ACTIVE]}
                            onChange={() =>
                              formikProps.setFieldValue(
                                CLIENT_FORM_FIELDS.ACTIVE,
                                !formikProps.values[CLIENT_FORM_FIELDS.ACTIVE]
                              )
                            }
                            data-testid="toggle-active"
                          />
                          <span className="toogle-text">Set as Active</span>
                          <FormikErrorMessage name={CLIENT_FORM_FIELDS.ACTIVE} />
                        </Col>
                      </Row>
                    </Section>
                  )}
                </Section>
              </Panel>
              <Panel className="panel-buttons">
                {renderFormButtons(formikProps)}
              </Panel>
            </Form>
          )}
        </Formik>
      }
    />
  )
}
export default CreateClient
